import { useRef } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

interface Review {
    id: number;
    author: string;
    text: string;
    rating: number;
}

const reviews: Review[] = [
    {
        id: 1,
        author: "Robinho dos Santos",
        text: "Top demais, Jean super gente boa e atencioso, e ensina muito bem, recomendo demais",
        rating: 5,
    },
    {
        id: 2,
        author: "Ezivania Alves",
        text: "Foi uma experiência incrível, o Jean Lucas e toda a sua equipe são maravilhosos, extremamente dedicados e grandes incentivadores. Aula prática sensacional, adrenalina pura, emoção total. E o mais legal, é que não para por aí, dia anterior a prova, o Jean se dispôs a dar mais uma aula teórica, que com certeza foi fundamental e muito enriquecedora. Parabéns Jean Lucas, parabéns equipe, os meus sinceros agradecimentos a cada um de vocês.",
        rating: 5,
    },
    {
        id: 3,
        author: "Matheus Marinho",
        text: "Não tenho palavras para expressar minha gratidão! Desde o início até o momento do meu exame final foi atenção total, tirou todas as minhas dúvidas, me senti inseguro mas durante a revisão sanou todas minhas dúvidas e no final deu tudo certo",
        rating: 5,
    },
    {
        id: 4,
        author: "Robson da Rosa",
        text: "Tive a excelente experiência ao realizar a transferência da minha embarcação com a JL Despachante e Escola Náutica. Todo o processo foi conduzido com muita agilidade, clareza e profissionalismo. Jean demonstrou conhecimento, seriedade e atenção em cada etapa, o que trouxe segurança e tranquilidade. Recomendo o trabalho dele a todos que buscam um serviço eficiente e de confiança.",
        rating: 5,
    },
    {
        id: 5,
        author: "Neemias Vinícius",
        text: "Fiz todo meu processo com a JL Despachante em São Francisco do Sul e foi simplesmente excelente! O instrutor Jean mandou muito bem — super paciente, atencioso e tirou todas as minhas dúvidas. Fiz minha habilitação de Arrais e também a documentação do meu barco com eles. Todo o time foi prestativo e resolveu tudo rápido, inclusive nas partes que envolvem a Marinha do Brasil. Preços acessíveis e formas de pagamento bem flexíveis. Recomendo demais, nota 10!",
        rating: 5,
    },
    {
        id: 6,
        author: "Raphael Dorneles",
        text: "Tive uma Ótima Experiência, com atendimento prestado pelo Jean. Serviço de registro de Embarcação, agilidade e clareza nas informações. Em resumo, um Ótimo Atendimento!!",
        rating: 5,
    },
];

const GoogleReviewsSection = () => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: "left" | "right") => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current;
            const scrollTo = direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth;
            scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
        }
    };

    return (
        <section className="relative py-24 bg-white overflow-hidden">
            {/* Background Soft Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] blur-[100px] rounded-full bg-gold/5 pointer-events-none" />

            <div className="container mx-auto px-6 lg:px-12 relative z-10">
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex items-center justify-center gap-3 mb-6"
                    >
                        <div className="flex text-gold">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-5 h-5 fill-current" />
                            ))}
                        </div>
                        <span className="text-slate-600 font-bold tracking-tight text-sm px-3 py-1 bg-gold/10 rounded-full border border-gold/20 leading-none">
                            +200 avaliações no Google
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-primary mb-6 leading-tight"
                    >
                        O que dizem nossos <span className="text-gold italic">Comandantes</span>
                    </motion.h2>
                    <p className="text-slate-500 max-w-2xl mx-auto text-lg">
                        Histórias reais de quem confiou na JL Escola Náutica para conquistar sua liberdade no mar.
                    </p>
                </div>

                {/* Mobile: Horizontal Carousel (hidden on md) */}
                <div className="md:hidden">
                    <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-8 -mx-4 px-4">
                        {reviews.map((review) => (
                            <motion.div
                                key={review.id}
                                className="min-w-[85vw] snap-start"
                            >
                                <div className="relative p-7 rounded-3xl bg-slate-50 border border-slate-100/80 hover:bg-white transition-all h-full flex flex-col">
                                    <Quote className="absolute top-6 right-8 w-10 h-10 text-gold/5" />

                                    <div className="flex text-gold mb-4">
                                        {[...Array(review.rating)].map((_, i) => (
                                            <Star key={i} className="w-3.5 h-3.5 fill-current" />
                                        ))}
                                    </div>

                                    <p className="text-slate-700 italic leading-relaxed mb-6 block relative z-10 text-base font-body flex-grow">
                                        "{review.text}"
                                    </p>

                                    <div className="flex items-center gap-3 pt-6 border-t border-slate-200/60 mt-auto">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center text-gold font-bold text-sm border border-gold/20">
                                            {review.author.charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="text-primary font-bold text-sm leading-tight">{review.author}</h4>
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Verificada</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                    {/* Visual Hint for Scroll */}
                    <div className="flex justify-center gap-1.5 mt-2">
                        {reviews.slice(0, 5).map((_, i) => (
                            <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-gold' : 'bg-slate-200'}`} />
                        ))}
                    </div>
                </div>

                {/* Desktop: Masonry Grid (hidden on mobile) */}
                <div className="hidden md:block">
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                        {reviews.map((review, index) => (
                            <motion.div
                                key={review.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="break-inside-avoid"
                            >
                                <div className="relative p-8 rounded-3xl bg-slate-50 border border-slate-100/80 hover:border-gold/30 hover:bg-white hover:shadow-2xl hover:shadow-gold/5 transition-all duration-500 group">
                                    <Quote className="absolute top-6 right-8 w-10 h-10 text-gold/5 group-hover:text-gold/10 transition-colors" />

                                    <div className="flex text-gold mb-4">
                                        {[...Array(review.rating)].map((_, i) => (
                                            <Star key={i} className="w-3.5 h-3.5 fill-current" />
                                        ))}
                                    </div>

                                    <p className="text-slate-700 italic leading-relaxed mb-8 block relative z-10 text-base md:text-lg font-body">
                                        "{review.text}"
                                    </p>

                                    <div className="flex items-center gap-3 pt-6 border-t border-slate-200/60">
                                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center text-gold font-bold text-base border border-gold/20">
                                            {review.author.charAt(0)}
                                        </div>
                                        <div>
                                            <h4 className="text-primary font-bold text-sm leading-tight">{review.author}</h4>
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Verificada</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="mt-16 text-center"
                >
                    <a
                        href="https://www.google.com/maps/place/JL+Despachante+e+Escola+N%C3%A1utica/@-26.2454643,-48.6399038,17z/data=!4m6!3m5!1s0x94d94f32b9b822f9:0xf9d3ce532e7d2b85!8m2!3d-26.2454643!4d-48.6399038!16s%2Fg%2F11h_yv5hn7!5m1!1e4?entry=ttu&g_ep=EgoyMDI2MDIyMy4wIKXMDSoASAFQAw%3D%3D"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-3 bg-primary text-white ml-2 px-10 py-4 rounded-full text-sm font-bold transition-all hover:scale-105 hover:bg-gold hover:text-primary shadow-xl shadow-primary/20"
                    >
                        Ver todas no Google
                        <svg
                            className="w-5 h-5 font-bold"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default GoogleReviewsSection;
